import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { Card, CardActions, CardContent, Tooltip } from '@mui/material';
import LoadingIconButton from 'core/components/LoadingIconButton';
import { intentionallyFloatingPromiseReturn } from 'core/utils/commonUtils';
import { printPdf } from 'core/utils/printUtils';
import share, { canShare } from 'core/utils/shareUtils';
import { observer } from 'mobx-react-lite';
import type { Document } from 'models/document/document';
import { DocumentTypeEnum } from 'models/enums/documentTypeEnum';
import type { FC } from 'react';
import { useContext, useState } from 'react';
import * as documentService from 'services/documentService';
import StoreContext from 'stores/context/StoreContext';
import { useOrderPageStore } from '../store/useOrderPageStore';
import DownloadButton from './DownloadButton';
import OrderDocumentIcon from './OrderDocumentIcon';
import OrderDocumentTitle from './OrderDocumentTitle';

const OrderDocumentCard: FC<DocumentCardProps> = observer(({ document, orderId }) => {
  const [printing, setPrinting] = useState(false);
  const [sharing, setSharing] = useState(false);

  const { notifyStore } = useContext(StoreContext);
  const orderPageStore = useOrderPageStore();

  const onPrintClickHandle: VoidFunction = () => {
    setPrinting(true);
    documentService
      .getDownloadDocumentAsync(document, orderId, DocumentTypeEnum.Pdf)
      .then((file) => {
        setPrinting(false);
        printPdf(file);

        return printing;
      })
      .catch((err) => {
        setPrinting(false);
        console.log(err);
      });
  };

  const onShareClickHandle = async (): Promise<void> => {
    setSharing(true);

    documentService
      .getDownloadDocumentAsync(document, orderId, DocumentTypeEnum.Pdf)
      .then(async (response: Blob) => {
        setSharing(false);

        const fileName = `${document.name}.pdf`;

        return await share({
          files: [new File([response], fileName, { type: response.type })],
          title: fileName,
          text: `Заказ ${orderPageStore.order?.number ?? ''}. ${document.name}`,
        });
      })
      .catch((err) => {
        notifyStore.showNotify('Не удалось поделиться документом', 'error');
        setSharing(false);

        console.log(err);
      });
  };

  const hasPdfDoc = document.documentTypes.includes(DocumentTypeEnum.Pdf);

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
      variant='outlined'>
      <CardContent
        sx={{ flexGrow: 1, rowGap: 1, display: 'flex', flexDirection: 'column' }}>
        <OrderDocumentIcon />
        <OrderDocumentTitle title={document.name} />
      </CardContent>
      <CardActions disableSpacing>
        <DownloadButton document={document} orderId={orderId} />

        {hasPdfDoc && (
          <Tooltip title='Печать'>
            <LoadingIconButton isLoading={printing} onClick={onPrintClickHandle}>
              <PrintOutlinedIcon />
            </LoadingIconButton>
          </Tooltip>
        )}

        {hasPdfDoc && canShare() && (
          <Tooltip title='Поделиться'>
            <LoadingIconButton
              isLoading={sharing}
              onClick={intentionallyFloatingPromiseReturn(onShareClickHandle)}>
              <ShareOutlinedIcon />
            </LoadingIconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
});

export interface DocumentCardProps {
  document: Document;
  orderId: number;
}

export default OrderDocumentCard;
